import { useNavigate } from "@tanstack/react-router";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { MAIN_DEFAULT_LOOK, type CharacterMood } from "../../shared/character";
import { Perso } from "../components/avatar";
import { Button, cx, Notice } from "../components/ui";
import { api, ApiError, errorText } from "../lib/api";
import { usePageTitle } from "../lib/hooks";
import { setMe } from "../lib/session";

/** The two fields of the page: 52 px pills on the panel color, a 1 px ring. */
const PILL_FIELD =
  "w-full rounded-full border-0 bg-elev px-5 text-[16px] text-fg shadow-[inset_0_0_0_1px_var(--border)] outline-none transition placeholder:text-subtle focus:shadow-[inset_0_0_0_1.5px_var(--fg-subtle)]";
const PILL_BUTTON = "h-[52px] w-full text-[16px]";
const QUIET = "inline-flex min-h-11 items-center justify-center px-2 text-[14px] text-subtle transition hover:text-fg disabled:opacity-60";

const RESEND_DELAY_MS = 30_000;

/** /connexion (V2Connexion) — passwordless login: the character, one field, « Continuer », then the 6-digit code. */
export function LoginPage({ next, invitation }: { next: string; invitation?: string }) {
  usePageTitle("Connexion");
  const navigate = useNavigate();
  const [step, setStep] = useState<"identifier" | "code">("identifier");
  const [identifier, setIdentifier] = useState("");
  const [sentTo, setSentTo] = useState<{ channel: "email" | "sms"; masked: string } | null>(null);
  const [code, setCode] = useState("");
  const [inviteCode, setInviteCode] = useState(invitation ?? "");
  const [needsInvite, setNeedsInvite] = useState(Boolean(invitation));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [resendAt, setResendAt] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [focused, setFocused] = useState(false);
  const [welcome, setWelcome] = useState(false);
  const codeRef = useRef<HTMLInputElement>(null);
  const idField = useId();
  const codeField = useId();
  const inviteField = useId();

  useEffect(() => {
    if (step !== "code") return;
    codeRef.current?.focus();
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [step]);

  async function sendCode(): Promise<boolean> {
    const value = identifier.trim();
    if (!value) {
      setError("Indique ton email ou ton numéro de téléphone.");
      return false;
    }
    if (value.length > 200) {
      setError("Cet identifiant est trop long.");
      return false;
    }
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const invite = inviteCode.trim().slice(0, 100);
      const res = await api("POST /api/auth/start", { body: { identifier: value, ...(invite ? { inviteCode: invite } : {}) } });
      setSentTo({ channel: res.channel, masked: res.masked });
      setResendAt(Date.now() + RESEND_DELAY_MS);
      setNow(Date.now());
      return true;
    } catch (err) {
      setError(errorText(err));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function onIdentifier(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    if (await sendCode()) {
      setCode("");
      setStep("code");
    }
  }

  async function onCode(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    const digits = code.replace(/\D/g, "");
    if (digits.length !== 6) {
      setError("Le code fait 6 chiffres.");
      return;
    }
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const invite = inviteCode.trim().slice(0, 100);
      const body = { identifier: identifier.trim(), code: digits, ...(invite ? { inviteCode: invite } : {}) };
      const { me } = await api("POST /api/auth/verify", { body });
      setWelcome(true);
      setMe(me);
      if (me.onboarded) await navigate({ href: next, replace: true });
      else await navigate({ to: "/bienvenue", replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setNeedsInvite(true);
        setError(`${err.message} Si tu as un code d'invitation, saisis-le ci-dessous.`);
      } else {
        setError(errorText(err));
      }
    } finally {
      setBusy(false);
    }
  }

  const canResend = now >= resendAt;
  // Asleep-ish at rest, listening while you type, pondering while the code is on its way, delighted once you're in.
  const mood: CharacterMood = welcome ? "joie" : busy ? "reflechit" : focused ? "ecoute" : step === "code" ? "reflechit" : "repos";
  const listen = { onFocus: () => setFocused(true), onBlur: () => setFocused(false) };

  const inviteInput = (
    <>
      <label htmlFor={inviteField} className="sr-only">
        Code d'invitation
      </label>
      <input
        id={inviteField}
        name="inviteCode"
        value={inviteCode}
        placeholder="Code d'invitation"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        maxLength={100}
        onChange={(e) => setInviteCode(e.target.value)}
        className={cx(PILL_FIELD, "h-[52px]")}
        {...listen}
      />
    </>
  );

  return (
    <div className="flex min-h-dvh flex-col items-center px-6 pt-[clamp(40px,11vh,90px)] pb-[34px] md:justify-center md:pt-10">
      <div className="flex w-full max-w-[342px] flex-1 flex-col items-center gap-[18px] md:flex-none">
        <Perso look={MAIN_DEFAULT_LOOK} mood={mood} size={170} label="Ariston" />
        <h1 className="sr-only">Connexion</h1>
        <p aria-hidden="true" className="font-display text-[30px] leading-none">
          {welcome ? "Te voilà." : "Ariston"}
        </p>

        <div aria-hidden="true" className="flex-1 md:h-10 md:flex-none" />

        {step === "identifier" ? (
          <form key="identifier" onSubmit={onIdentifier} noValidate className="cv-in flex w-full flex-col gap-2.5">
            <label htmlFor={idField} className="sr-only">
              E-mail ou numéro
            </label>
            <input
              id={idField}
              name="identifier"
              type="text"
              inputMode="email"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder="E-mail ou numéro"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className={cx(PILL_FIELD, "h-[52px]")}
              autoFocus
              {...listen}
            />
            {needsInvite ? inviteInput : null}
            <Button type="submit" size="lg" className={PILL_BUTTON} busy={busy}>
              Continuer
            </Button>
            {!needsInvite ? (
              <button type="button" className={QUIET} onClick={() => setNeedsInvite(true)}>
                J'ai un code d'invitation
              </button>
            ) : null}
          </form>
        ) : (
          <form key="code" onSubmit={onCode} noValidate className="cv-in flex w-full flex-col items-center gap-2.5">
            <label htmlFor={codeField} className="text-center text-[14px] text-muted">
              {sentTo ? `Code envoyé ${sentTo.channel === "sms" ? "au" : "à"} ${sentTo.masked}` : "Code envoyé"}
            </label>
            <input
              id={codeField}
              ref={codeRef}
              name="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="••••••"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className={cx(PILL_FIELD, "h-14 px-6 text-center font-mono text-[24px] tracking-[0.5em] placeholder:tracking-[0.5em]")}
              {...listen}
            />
            {needsInvite ? inviteInput : null}
            <Button type="submit" size="lg" className={PILL_BUTTON} busy={busy}>
              Entrer
            </Button>
            <div className="flex flex-wrap items-center justify-center gap-x-4">
              <button
                type="button"
                className={QUIET}
                disabled={!canResend || busy}
                onClick={async () => {
                  if (await sendCode()) setInfo("Nouveau code envoyé.");
                }}
              >
                {canResend ? "Renvoyer" : `Renvoyer (${Math.ceil((resendAt - now) / 1000)} s)`}
              </button>
              <button
                type="button"
                className={QUIET}
                onClick={() => {
                  setStep("identifier");
                  setError(null);
                  setInfo(null);
                }}
              >
                Changer d'identifiant
              </button>
            </div>
          </form>
        )}
        {error ? (
          <Notice tone="danger" role="alert" className="w-full">
            {error}
          </Notice>
        ) : info ? (
          <p role="status" className="text-[13.5px] text-muted">
            {info}
          </p>
        ) : null}
      </div>
    </div>
  );
}
